import { useState, useEffect } from "react";
import Swal from "sweetalert2";
import {
  startAuthentication,
  startRegistration,
} from "@simplewebauthn/browser";
import api from "../services/api";

interface UseBiometricAuthSellerParams {
  pluginspaceId?: string;
  getToken: () => Promise<string | void>;
  config: any;
  onBiometricLoginSuccess: (
    payload: any,
    documentUsed: string,
  ) => Promise<void>;
  setLoading?: (value: boolean) => void;
}

const BIOMETRIC_LOGIN_STORAGE_KEY = "seller_biometric_login";

export function useBiometricAuthSeller({
  pluginspaceId,
  getToken,
  config,
  onBiometricLoginSuccess,
  setLoading,
}: UseBiometricAuthSellerParams) {
  const [biometricDocSaved, setBiometricDocSaved] = useState<string>("");

  const isBiometricEnabledAdmin =
    !!config?.content?.pdv?.biometric_authentication;
  const biometriaDisponivel =
    typeof window !== "undefined" &&
    !!window.PublicKeyCredential &&
    isBiometricEnabledAdmin;

  function saveBiometricPreference(doc: string) {
    if (!doc || !pluginspaceId) return;
    const cleanDoc = doc.replace(/\D/g, "");

    localStorage.setItem(
      BIOMETRIC_LOGIN_STORAGE_KEY,
      JSON.stringify({ pluginspaceId, doc: cleanDoc }),
    );
    setBiometricDocSaved(cleanDoc);
  }

  function removeBiometricPreference() {
    localStorage.removeItem(BIOMETRIC_LOGIN_STORAGE_KEY);
    setBiometricDocSaved("");
  }

  function loadBiometricPreference() {
    if (!pluginspaceId) {
      setBiometricDocSaved("");
      return;
    }
    const stored = localStorage.getItem(BIOMETRIC_LOGIN_STORAGE_KEY);
    if (!stored) {
      setBiometricDocSaved("");
      return;
    }
    try {
      const parsed = JSON.parse(stored);
      if (parsed?.pluginspaceId === pluginspaceId && parsed?.doc) {
        setBiometricDocSaved(parsed.doc);
        return;
      }
    } catch (error) {}
    setBiometricDocSaved("");
  }

  async function offerBiometricSetup(
    sellerToken: string,
    sellerId: number,
    loginDoc: string,
    skipConfirm: boolean = false,
  ): Promise<void> {
    const cleanDoc = loginDoc.replace(/\D/g, "");

    if (!skipConfirm && cleanDoc === biometricDocSaved) {
      return;
    }

    if (!skipConfirm) {
      const result = await Swal.fire({
        title: "Facilite seu acesso!",
        text: "Deseja ativar a biometria neste dispositivo para acessar o painel de vendas mais rapidamente?",
        icon: "info",
        showCancelButton: true,
        confirmButtonText: "Sim, ativar",
        cancelButtonText: "Agora não",
        confirmButtonColor:
          config?.webappcolor?.webappbutton &&
          config.webappcolor.webappbutton !== "default" &&
          config.webappcolor.webappbutton !== ""
            ? config.webappcolor.webappbutton
            : "#000000",
        cancelButtonColor: "#d33",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showLoaderOnConfirm: true,
        preConfirm: () => {
          return new Promise((resolve) => setTimeout(resolve, 300));
        },
        customClass: {
          confirmButton: "text-white",
        },
      });

      if (!result.isConfirmed) return;
    }

    try {
      const registerOptions = await api
        .get(
          `api/customer-authenticator/register-options/${sellerId}?context=seller`,
          { headers: { Authorization: `Bearer ${sellerToken}` } },
        )
        .then((res) => res.data)
        .catch((err) => err.response?.data);

      if (!registerOptions?.success) {
        if (registerOptions?.message?.includes("já possui")) {
          const existingAuths = await api
            .get(
              `api/customer-authenticator/customer/${sellerId}?context=seller`,
              { headers: { Authorization: `Bearer ${sellerToken}` } },
            )
            .then((res) => res.data)
            .catch(() => null);

          if (existingAuths?.data && existingAuths.data.length > 0) {
            for (const auth of existingAuths.data) {
              await api.delete(
                `api/customer-authenticator/delete/${sellerId}/${auth.credentialID}?context=seller`,
                { headers: { Authorization: `Bearer ${sellerToken}` } },
              );
            }
            return offerBiometricSetup(sellerToken, sellerId, loginDoc, true);
          }
          return;
        }

        throw new Error(
          registerOptions?.message || "Falha ao iniciar cadastro da biometria.",
        );
      }

      const attResp = await startRegistration(registerOptions.data);

      const verifyRegister = await api
        .post(
          `api/customer-authenticator/register-verify/${sellerId}?context=seller`,
          attResp,
          { headers: { Authorization: `Bearer ${sellerToken}` } },
        )
        .then((res) => res.data)
        .catch((err) => err.response?.data);

      if (verifyRegister?.success) {
        saveBiometricPreference(loginDoc);
        Swal.fire({
          icon: "success",
          title: "Sucesso",
          text: "Biometria ativada com sucesso.",
          timer: 1500,
          showConfirmButton: false,
        });
        return;
      }

      throw new Error(
        verifyRegister?.message || "Não foi possível ativar a biometria.",
      );
    } catch (error: any) {
      if (error?.name === "NotAllowedError") {
        removeBiometricPreference();
        Swal.fire({
          icon: "warning",
          title: "Atenção",
          text: "Ativação cancelada. Você continuará usando senha.",
          timer: 3000,
        });
        return;
      }
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: error?.message || "Erro ao ativar biometria neste dispositivo.",
      });
    }
  }

  async function loginWithBiometric(cpfInput: string = "") {
    try {
      const documentToUse = cpfInput.replace(/\D/g, "") || biometricDocSaved;

      if (!documentToUse) {
        Swal.fire({
          icon: "warning",
          text: "Informe o CPF para usar biometria.",
        });
        return;
      }

      setLoading?.(true);
      const pluginspaceToken = await getToken();

      const loginOptions = await api
        .post(
          `api/customer-authenticator/login-options`,
          { doc: documentToUse, loginContext: "seller" },
          { headers: { Authorization: `Bearer ${pluginspaceToken}` } },
        )
        .then((res) => res.data)
        .catch((err) => err.response?.data);

      if (!loginOptions?.success) {
        throw new Error(loginOptions?.message || "Falha ao iniciar biometria.");
      }

      const authResponse = await startAuthentication(loginOptions.data);

      const loginVerify = await api
        .post(
          `api/customer-authenticator/login-verify`,
          {
            doc: documentToUse,
            response: authResponse,
            loginContext: "seller",
          },
          { headers: { Authorization: `Bearer ${pluginspaceToken}` } },
        )
        .then((res) => res.data)
        .catch((err) => err.response?.data);

      if (!loginVerify?.success || !loginVerify?.data?.token) {
        throw new Error(loginVerify?.message || "Biometria não validada.");
      }

      await onBiometricLoginSuccess(loginVerify, documentToUse);

      saveBiometricPreference(documentToUse);
    } catch (error: any) {
      if (error?.name === "NotAllowedError") {
        Swal.fire({ icon: "info", text: "Leitura cancelada. Use a senha." });
      } else if (
        error?.message?.includes("Nenhuma biometria cadastrada") ||
        error?.message?.includes("não encontrado")
      ) {
        removeBiometricPreference();
        Swal.fire({
          icon: "warning",
          text: "Biometria indisponível. Faça login com senha para configurar novamente.",
        });
      } else {
        Swal.fire({
          icon: "error",
          text: error?.message || "Erro ao entrar com biometria.",
        });
      }
    } finally {
      setLoading?.(false);
    }
  }

  useEffect(() => {
    loadBiometricPreference();
  }, [pluginspaceId]);

  return {
    biometriaDisponivel,
    offerBiometricSetup,
    loginWithBiometric,
    removeBiometricPreference,
    biometricDocSaved,
  };
}
